
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { EntityService } from '@services/core/entity.service';
import { ModuleService } from '@services/core/module.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { modulesOfGroup, currentAuditors, seedAuditors, pruneAuditors, auditorRecord } from './edit-entity.helper';

@Component({
	selector: 'app-edit-entity',
  templateUrl: './editEntity.component.html',
  styleUrls: ["./editEntity.component.css"],
     imports: [PanelComponent, ReactiveFormsModule, NgSelectComponent, FormsModule],
	changeDetection: ChangeDetectionStrategy.Default,
	standalone: true,
})
export class EditEntityComponent implements OnInit{
    @Input() entity: any
    @Output() close = new EventEmitter()
    @Output() update = new EventEmitter()
    private entityService = inject(EntityService)
    private moduleService = inject(ModuleService)
	private _fb = inject(FormBuilder)
    private destroyRef = inject(DestroyRef)
    public entityForm: FormGroup
    protected groups
    protected offices
    protected modulesForGroup: any[] = []
    protected auditors: any[] = []
    protected auditorsByModule: Record<string, string | null> = {}
    protected compareById = (a: any, b: any) => a === b || (a?.id ?? a) === (b?.id ?? b);

	ngOnInit(): void {
        this.buildForm()
        this.loadFormData()
	}

    private buildForm() {
        this.entityForm = this._fb.group({
            id: [null],
            name: [null, [Validators.required]],
            code: [null, [Validators.required]],
            groupId: [null, [Validators.required]],
            officeId: [null, [Validators.required]],
            enabled: [true]
        })
    }

    private loadFormData() {
        forkJoin({
            groups: this.entityService.getGroupsEntities(),
            offices: this.entityService.getOffices(),
            modules: this.moduleService.getModules(),
            auditors: this.entityService.getAuditors(),
        }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => this.applyFormData(res));
    }

    private applyFormData(res: any) {
        this.groups = res.groups;
        this.offices = res.offices;
        this.auditors = res.auditors || [];
        this.allModules = res.modules || [];
        this.initFromEntity();
        this.watchGroupChanges();
    }

    private initFromEntity() {
        if (this.entity) this.setForm();
        this.refreshModulesForGroup(this.entityForm.get('groupId')?.value);
    }

    private watchGroupChanges() {
        this.entityForm.get('groupId')?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((id) => this.refreshModulesForGroup(id));
    }

    private allModules: any[] = []

    private refreshModulesForGroup(groupId: string | null) {
        if (this.missingGroupData(groupId)) return this.clearGroupModules();
        this.modulesForGroup = modulesOfGroup(this.allModules, groupId);
        this.syncModuleAuditors();
    }

    private missingGroupData(groupId: string | null) {
        if (!groupId) return true;
        return this.allModules.length === 0;
    }

    private clearGroupModules() {
        this.modulesForGroup = [];
    }

    private syncModuleAuditors() {
        const existing = currentAuditors(this.entity);
        seedAuditors(this.auditorsByModule, this.modulesForGroup, existing);
        pruneAuditors(this.auditorsByModule, this.modulesForGroup);
    }

    setForm(){
        this.entityForm.patchValue({
            id: this.entity.id,
            name: this.entity.name,
            code: this.entity.code,
            groupId: this.entity.group?.id,
            officeId: this.entity.office?.id,
            enabled: this.entity.enabled
        })
        this.auditorsByModule = auditorRecord(this.entity);
    }

    save(){
        const groupId = this.entityForm.get('groupId')?.value;
        const moduleAuditors = this.modulesForGroup.map((m: any) => ({
            moduleId: m.id,
            groupId,
            auditorId: this.auditorsByModule[m.id] || null
        }));
        const payload = { ...this.entityForm.value, moduleAuditors };
        this.entityService.updateEntity(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
            this.update.emit()
        })
    }

    cancel(){
        this.close.emit()
    }
  
}
